import { defineStore } from 'pinia'
import * as sdk from '@/helpers/sdk'

import useProfileStore, { profileKey } from '@/stores/profile.store'
import useRouterStore from '@/stores/router.store'
import profileQueue from '@/helpers/profileQueue'
import getRedirectAfterDelete from '@/helpers/profile/getRedirectAfterDelete'
import routeNames from '@/router/names'
import useAction from '@/composables/useAction'

const meta = {
  requestPermission: {
    read: true,
    edit: true,
  },
}

async function createGlobalPreset(payload = {}) {
  const profileStore = useProfileStore()
  const routerStore = useRouterStore()

  const profile = profileStore[profileKey]

  const {
    channel,
    presetRange,
    presets,
  } = profileStore // current

  let newOrder = 0
  const order = Array.isArray(payload.presets) ? payload.presets[0]?._order : payload._order
  if (typeof order === 'number') {
    newOrder = order
  } else if (presets) {
    newOrder = presets.length
  }

  const result = await profileQueue.add(
    () => sdk.v3.profiles.global.createPreset({
      url: {
        profileUid: profile.uid,
        cUid: channel.data._uid,
        prUid: presetRange.data._uid,
      },
      body: payload,
    }),
  ).result

  profileStore[profileKey] = result.data

  // Redirect based on index, to select the newly created preset
  await routerStore.push({
    name: routeNames.GlobalPreset,
    params: {
      profileUid: profileStore.profileUid,
      channelUid: profileStore.channelUid,
      presetRangeUid: profileStore.presetRangeUid,
      presetUid: newOrder,
    },
  })

  return result.data
}

async function createLocalPreset(payload = {}) {
  const profileStore = useProfileStore()
  const routerStore = useRouterStore()

  const profile = profileStore[profileKey]

  const {
    mode,
    channel,
    presetRange,
    presets,
  } = profileStore // current

  // Cache the current preset range/preset indexes
  const presetRangeIndex = presetRange && presetRange.data._order

  let newOrder = 0
  const order = Array.isArray(payload.presets) ? payload.presets[0]?._order : payload._order
  if (typeof order === 'number') {
    newOrder = order
  } else if (presets) {
    newOrder = presets.length
  }

  const result = await profileQueue.add(
    () => sdk.v3.profiles.modes.createPreset({
      url: {
        profileUid: profile.uid,
        mUid: mode.data._uid,
        cUid: channel.data._uid,
        prUid: presetRange.data._uid,
      },
      body: payload,
    }),
  ).result

  profileStore[profileKey] = result.data

  // Redirect based on index, to select the newly created preset
  await routerStore.push({
    name: routeNames.LocalPreset,
    params: {
      profileUid: profileStore.profileUid,
      modeUid: profileStore.modeUid,
      channelUid: profileStore.channelUid,
      presetRangeUid: presetRangeIndex,
      presetUid: newOrder,
    },
  })

  return result.data
}

async function duplicateGlobalPreset({ _uid } = {}) {
  const profileStore = useProfileStore()
  const routerStore = useRouterStore()

  const profile = profileStore[profileKey]

  const {
    channel,
    presetRange,
    preset,
  } = profileStore // current

  const newOrder = preset.data._order + 1

  const result = await profileQueue.add(
    () => sdk.v3.profiles.global.copyPreset({
      url: {
        profileUid: profile.uid,
        cUid: channel.data._uid,
        prUid: presetRange.data._uid,
        pUid: (
          _uid || preset.data._uid
        ),
      },
    }),
  ).result

  profileStore[profileKey] = result.data

  // Redirect based on index, to select the copied preset
  await routerStore.push({
    name: routeNames.GlobalPreset,
    params: {
      profileUid: profileStore.profileUid,
      channelUid: profileStore.channelUid,
      presetRangeUid: profileStore.presetRangeUid,
      presetUid: newOrder,
    },
  })

  return result.data
}

async function duplicateLocalPreset({ _uid } = {}) {
  const profileStore = useProfileStore()
  const routerStore = useRouterStore()

  const profile = profileStore[profileKey]

  const {
    mode,
    channel,
    presetRange,
    preset,
  } = profileStore // current

  // Cache the current preset range/preset indexes
  const presetRangeIndex = presetRange && presetRange.data._order

  const newOrder = preset.data._order + 1

  const result = await profileQueue.add(
    () => sdk.v3.profiles.modes.copyPreset({
      url: {
        profileUid: profile.uid,
        mUid: mode.data._uid,
        cUid: channel.data._uid,
        prUid: presetRange.data._uid,
        pUid: (
          _uid || preset.data._uid
        ),
      },
    }),
  ).result

  profileStore[profileKey] = result.data

  // Redirect based on index, to select the copied preset
  await routerStore.push({
    name: routeNames.LocalPreset,
    params: {
      profileUid: profileStore.profileUid,
      modeUid: profileStore.modeUid,
      channelUid: profileStore.channelUid,
      presetRangeUid: presetRangeIndex,
      presetUid: newOrder,
    },
  })

  return result.data
}

async function editLocalPreset({ _uid, ...payload }) {
  const profileStore = useProfileStore()

  const profile = profileStore[profileKey]
  const {
    mode,
    channel,
    presetRange,
    preset,
  } = profileStore // current

  const result = await profileQueue.add(
    () => sdk.v3.profiles.modes.editPreset({
      url: {
        profileUid: profile.uid,
        mUid: mode.data._uid,
        cUid: channel.data._uid,
        prUid: presetRange.data._uid,
        pUid: (
          _uid || preset.data._uid
        ),
      },
      body: payload,
    }),
  ).result

  profileStore[profileKey] = result.data

  return result.data
}

async function editGlobalPreset({ _uid, ...payload }) {
  const profileStore = useProfileStore()

  const profile = profileStore[profileKey]
  const {
    globalChannel,
    globalPresetRange,
    globalPreset,
  } = profileStore

  const result = await profileQueue.add(
    () => sdk.v3.profiles.global.editPreset({
      url: {
        profileUid: profile.uid,
        cUid: globalChannel.data._uid,
        prUid: globalPresetRange.data._uid,
        pUid: (
          _uid || globalPreset.data._uid
        ),
      },
      body: payload,
    }),
  ).result

  profileStore[profileKey] = result.data
  return result.data
}

async function deleteGlobalPreset({ _uid } = {}) {
  const profileStore = useProfileStore()
  const routerStore = useRouterStore()

  const profile = profileStore[profileKey]

  const {
    channel,
    presetRange,
    preset,
  } = profileStore // current

  const redirectTo = getRedirectAfterDelete(preset)

  const result = await profileQueue.add(
    () => sdk.v3.profiles.global.deletePreset({
      url: {
        profileUid: profile.uid,
        cUid: channel.data._uid,
        prUid: presetRange.data._uid,
        pUid: (
          _uid || preset.data._uid
        ),
      },
    }),
  ).result

  profileStore[profileKey] = result.data

  // Redirect to closest preset
  await routerStore.push(redirectTo)

  return result.data
}

async function deleteLocalPreset({ _uid } = {}) {
  const profileStore = useProfileStore()
  const routerStore = useRouterStore()

  const profile = profileStore[profileKey]

  const {
    mode,
    channel,
    presetRange,
    preset,
  } = profileStore // current

  const redirectTo = getRedirectAfterDelete(preset)

  const result = await profileQueue.add(
    () => sdk.v3.profiles.modes.deletePreset({
      url: {
        profileUid: profile.uid,
        mUid: mode.data._uid,
        cUid: channel.data._uid,
        prUid: presetRange.data._uid,
        pUid: (
          _uid || preset.data._uid
        ),
      },
    }),
  ).result

  profileStore[profileKey] = result.data

  // Redirect to closest preset
  await routerStore.push(redirectTo)

  return result.data
}

async function replaceLocalPreset({ _uid, ...payload }) {
  const profileStore = useProfileStore()
  const routerStore = useRouterStore()

  const profile = profileStore[profileKey]
  const {
    mode,
    channel,
    presetRange,
    preset,
    globalPresetRange,
    globalPreset,
  } = profileStore // current

  const result = await profileQueue.add(
    () => sdk.v3.profiles.modes.replacePreset({
      url: {
        profileUid: profile.uid,
        mUid: mode.data._uid,
        cUid: channel.data._uid,
        prUid: presetRange.data._uid,
        pUid: (
          _uid || preset.data._uid
        ),
      },
      body: payload,
    }),
  ).result

  profileStore[profileKey] = result.data

  // Redirect based on index if the presetRange and/or preset are
  // not local, since after any edit on a preset we're supposed to be on a local
  // presetRange and preset
  await routerStore.replace({
    name: routeNames.LocalPreset,
    params: {
      profileUid: profileStore.profileUid,
      modeUid: profileStore.modeUid,
      channelUid: profileStore.channelUid,
      presetRangeUid: (
        globalPresetRange ? globalPresetRange.data._order : profileStore.presetRangeUid
      ),
      presetUid: (
        globalPreset ? globalPreset.data._order : profileStore.presetUid
      ),
    },
  })

  return result.data
}

async function replaceGlobalPreset({ _uid, ...payload }) {
  const profileStore = useProfileStore()
  const routerStore = useRouterStore()

  const profile = profileStore[profileKey]
  const {
    globalChannel,
    globalPresetRange,
    globalPreset,
  } = profileStore

  const result = await profileQueue.add(
    () => sdk.v3.profiles.global.replacePreset({
      url: {
        profileUid: profile.uid,
        cUid: globalChannel.data._uid,
        prUid: globalPresetRange.data._uid,
        pUid: (
          _uid || globalPreset.data._uid
        ),
      },
      body: payload,
    }),
  ).result

  // Redirect based on index, to select the newly created preset
  await routerStore.push({
    name: routeNames.GlobalPreset,
    params: {
      profileUid: profileStore.profileUid,
      channelUid: profileStore.channelUid,
      presetRangeUid: profileStore.presetRangeUid,
      presetUid: profileStore.presetUid,
    },
  })

  profileStore[profileKey] = result.data
  return result.data
}

const usePresetStore = defineStore('profile/preset', () => {
  const { action: createGlobal, status } = useAction(createGlobalPreset, { meta })

  return {
    status,
    createGlobal,
    createLocal: useAction(createLocalPreset, { status, meta }).action,
    duplicateGlobal: useAction(duplicateGlobalPreset, { status, meta }).action,
    duplicateLocal: useAction(duplicateLocalPreset, { status, meta }).action,
    editGlobal: useAction(editGlobalPreset, { status, meta }).action,
    editLocal: useAction(editLocalPreset, { status, meta }).action,
    deleteGlobal: useAction(deleteGlobalPreset, { status, meta }).action,
    deleteLocal: useAction(deleteLocalPreset, { status, meta }).action,
    replaceGlobal: useAction(replaceGlobalPreset, { status, meta }).action,
    replaceLocal: useAction(replaceLocalPreset, { status, meta }).action,
  }
})

export default usePresetStore
